/** Files an operator attaches to an AI diagnose question.
 *
 *  Uploads go through the API (one file per request, multipart) rather than
 *  straight to S3: the console CSP's `connect-src` has no S3 host, and the
 *  backend decides the file's type from its bytes. Reads come back through
 *  the API too — gated by the AI permission of the thread's task type — and
 *  are turned into `blob:` URLs locally (the CSP allows `blob:` images). */

import { client, ConsoleApiError } from './client';
import type { components } from './types';

export type DiagnoseAttachment = components['schemas']['DiagnoseAttachment'];
export type DiagnoseThreadAttachment = components['schemas']['DiagnoseThreadAttachment'];
export type DiagnoseAttachmentCaps = components['schemas']['DiagnoseAttachmentCaps'];
export type DiagnoseAttachmentKind = DiagnoseAttachment['kind'];

/** `original` = the uploaded bytes; `preview` = images only, the PNG/JPEG the
 *  model sees; `text` = text-bearing kinds, extracted UTF-8 (≤ 2 MiB). */
export type AttachmentVariant = 'original' | 'preview' | 'text';

/** A 30 MB upload on a slow link outlives the client's 30 s default. */
const UPLOAD_TIMEOUT_MS = 10 * 60_000;
const READ_TIMEOUT_MS = 2 * 60_000;

export interface UploadOptions {
  signal?: AbortSignal;
  /** 0..1 as the request body leaves the browser. */
  onProgress?: (fraction: number) => void;
}

/** POST /diagnose/attachments/ — stores one file, returns its descriptor.
 *  The id goes into the start request's `attachments`. */
export async function uploadAttachment(file: File, opts: UploadOptions = {}): Promise<DiagnoseAttachment> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const { data } = await client.post<DiagnoseAttachment>('/diagnose/attachments/', fd, {
    // Let axios set the multipart boundary itself.
    headers: { 'Content-Type': undefined },
    signal: opts.signal,
    timeout: UPLOAD_TIMEOUT_MS,
    onUploadProgress: (e) => {
      if (opts.onProgress && e.total) opts.onProgress(Math.min(1, e.loaded / e.total));
    },
  });
  return data;
}

const attachmentUrl = (taskId: string, attachmentId: string) =>
  `/diagnose/${taskId}/attachments/${attachmentId}/`;

/** The bytes of one variant. `taskId` may be any member of the thread. */
export async function fetchAttachmentBlob(
  taskId: string,
  attachmentId: string,
  variant: AttachmentVariant,
): Promise<Blob> {
  const { data } = await client.get<Blob>(attachmentUrl(taskId, attachmentId), {
    params: { variant },
    responseType: 'blob',
    timeout: READ_TIMEOUT_MS,
  });
  return data;
}

/** The bytes of one variant as an ArrayBuffer (for embedding, e.g. the PDF
 *  export). */
export async function fetchAttachmentBytes(
  taskId: string,
  attachmentId: string,
  variant: AttachmentVariant,
): Promise<ArrayBuffer> {
  const { data } = await client.get<ArrayBuffer>(attachmentUrl(taskId, attachmentId), {
    params: { variant },
    responseType: 'arraybuffer',
    timeout: READ_TIMEOUT_MS,
  });
  return data;
}

/** The extracted text of a text / document / PDF attachment. `truncated` =
 *  the server cut it at its 2 MiB preview cap. */
export async function fetchAttachmentText(
  taskId: string,
  attachmentId: string,
): Promise<{ text: string; truncated: boolean }> {
  const res = await client.get<string>(attachmentUrl(taskId, attachmentId), {
    params: { variant: 'text' },
    // 'text' (not 'json'): axios must not JSON-parse an attached .json file.
    responseType: 'text',
    timeout: READ_TIMEOUT_MS,
  });
  const truncated = String(res.headers?.['x-cp-truncated'] ?? '') === '1';
  return { text: res.data, truncated };
}

/** 410 — the S3 lifecycle removed the file (kept for one year). */
export function isAttachmentExpired(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 410;
}

/** Which variant the console can show inline for a kind. */
export function inlineVariant(kind: DiagnoseAttachmentKind): 'preview' | 'text' | null {
  if (kind === 'image') return 'preview';
  if (kind === 'text' || kind === 'document' || kind === 'pdf') return 'text';
  return null;
}
